import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client';
import { useLocation, useNavigate } from 'react-router-dom';
import classNames from 'classnames';
import { Button } from 'components/Button';
import { NonNegativeNumberInput } from 'components/NonNegativeNumberInput';
import { ClaimsImagesUpload } from 'components/ClaimsImagesUpload';
import { useStore } from 'context/store-context';
import { formatReturnDecimal } from 'utils/returnMoney';
import { isMixedReturnCurrency, projectReturnOrderMoney } from 'utils/returnPresentmentMoney';
import {
  GET_NEW_REQUEST_ORDERS,
  GET_NEW_REQUEST_ORDER,
  GET_NEW_RETURN_POLICY,
} from 'gql/newRequest';
import { START_CLAIM, START_RETURN } from 'gql/mutations';
import {
  NewRequestItemInput,
  NewReturnPolicy,
  RequestOrder,
  RequestOrderItem,
} from 'types/newRequest';
import { Order } from 'types/order';
import {
  getNewRequestReturnsEnabled,
  getNewReturnReasons,
  getNewReturnEvidence,
  isDefectReturn,
  isCompletedReturnPhoto,
} from 'utils/newRequestPolicy';
import {
  getRequestReturnTo,
  withRequestReturnTo,
} from 'utils/requestNavigation';
import { NewIssueResolution } from './NewIssueResolution';
import {
  ReturnRequestOptions,
  ReturnRequestSelection,
} from './ReturnRequestOptions';

type RequestType = 'ISSUE' | 'RETURN';
type Step =
  | 'Order'
  | 'Request type'
  | 'Items & details'
  | 'Return options'
  | 'Review request';
const fieldClass =
  'mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:ring-blue-500';
const issueReasons = [
  { id: 'LOST', label: 'Lost' },
  { id: 'STOLEN', label: 'Stolen' },
  { id: 'DAMAGED', label: 'Damaged' },
  { id: 'LATE', label: 'Late' },
  { id: 'OTHER', label: 'Other' },
];
const errorMessage = (error: unknown) =>
  error instanceof Error
    ? error.message
    : 'Something went wrong. Please try again.';

function issueAvailableQuantity(item: RequestOrderItem) {
  const claims = (item.claimItems || [])
    .filter(
      ({ claim }) =>
        !['DENIED', 'ARCHIVED'].includes(claim?.status?.toUpperCase() || ''),
    )
    .reduce(
      (total, allocation) =>
        total + Number(allocation.quantity ?? item.quantity),
      0,
    );
  const returns = (item.returnItems || [])
    .filter(
      ({ returnRecord }) =>
        !['DENIED', 'EXPIRED'].includes(
          returnRecord?.status?.toUpperCase() || '',
        ),
    )
    .reduce(
      (total, allocation) =>
        total + Number(allocation.quantity ?? item.quantity),
      0,
    );
  return Math.max(0, item.quantity - claims - returns);
}

function NewRequestFlow() {
  const { storeId, storeProperties } = useStore();
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state as {
    order?: Order;
    returnLocation?: string;
  } | null;
  const initialOrderId = state?.order?.id;
  const returnTo = getRequestReturnTo(
    location.search,
    state?.returnLocation,
    '/requests',
  );
  const [step, setStep] = useState<Step>('Order');
  const [type, setType] = useState<RequestType | null>(null);
  const [search, setSearch] = useState('');
  const [orders, setOrders] = useState<RequestOrder[]>([]);
  const [order, setOrder] = useState<RequestOrder | null>(null);
  const [returnMoneyOrder, setReturnMoneyOrder] = useState<RequestOrder | null>(null);
  const [orderIdToLoad, setOrderIdToLoad] = useState(initialOrderId);
  const [items, setItems] = useState<NewRequestItemInput[]>([]);
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [images, setImages] = useState<string[]>([]);
  const [policy, setPolicy] = useState<NewReturnPolicy | null>(null);
  const [returnSelection, setReturnSelection] =
    useState<ReturnRequestSelection | null>(null);
  const [error, setError] = useState('');
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [loadingPolicy, setLoadingPolicy] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [claimId, setClaimId] = useState('');
  const submissionLock = useRef(false);
  const lookupVersion = useRef(0);
  const policyVersion = useRef(0);
  const [findOrders] = useLazyQuery(GET_NEW_REQUEST_ORDERS);
  const [getOrder] = useLazyQuery(GET_NEW_REQUEST_ORDER);
  const [getPolicy] = useLazyQuery(GET_NEW_RETURN_POLICY);
  const [startClaim] = useMutation(START_CLAIM);
  const [startReturn] = useMutation(START_RETURN);
  const invalidatePolicy = useCallback(() => {
    policyVersion.current++;
  }, []);
  const invalidateLookups = useCallback(() => {
    lookupVersion.current++;
    invalidatePolicy();
  }, [invalidatePolicy]);

  const loadOrders = useCallback(
    async (value: string) => {
      const version = ++lookupVersion.current;
      setLoadingOrders(true);
      setError('');
      setOrders([]);
      try {
        const result = await findOrders({
          variables: { storeId, search: `%${value.trim()}%`, limit: 10 },
          fetchPolicy: 'network-only',
        });
        if (version !== lookupVersion.current) return;
        if (result.error) throw result.error;
        setOrders(result.data?.orders || []);
      } catch (error) {
        if (version === lookupVersion.current) setError(errorMessage(error));
      } finally {
        if (version === lookupVersion.current) setLoadingOrders(false);
      }
    },
    [findOrders, storeId],
  );

  const loadOrder = useCallback(
    async (id: string) => {
      const version = ++lookupVersion.current;
      setOrderIdToLoad(id);
      setLoadingOrders(true);
      setError('');
      try {
        const result = await getOrder({
          variables: { storeId, id },
          fetchPolicy: 'network-only',
        });
        if (version !== lookupVersion.current) return;
        if (result.error) throw result.error;
        const selected = result.data?.orders?.[0] as RequestOrder | undefined;
        if (!selected || selected.storeId !== storeId)
          throw new Error(
            'This order could not be found in the current store.',
          );
        setOrder(selected);
        setItems([]);
        setType(null);
        setReason('');
        setNotes('');
        setImages([]);
        setPolicy(null);
        setReturnMoneyOrder(null);
        setReturnSelection(null);
        setStep('Request type');
      } catch (error) {
        if (version === lookupVersion.current) setError(errorMessage(error));
      } finally {
        if (version === lookupVersion.current) setLoadingOrders(false);
      }
    },
    [getOrder, storeId],
  );

  useEffect(() => {
    if (!storeId) return;
    if (initialOrderId) loadOrder(initialOrderId);
    else loadOrders('');
    return invalidateLookups;
  }, [storeId, initialOrderId, loadOrder, loadOrders, invalidateLookups]);

  const loadPolicy = useCallback(async () => {
    if (!order) return;
    const version = ++policyVersion.current;
    setLoadingPolicy(true);
    setPolicy(null);
    setReturnMoneyOrder(null);
    setError('');
    setReturnSelection(null);
    try {
      const result = await getPolicy({
        variables: { input: { storeId, orderId: order.id } },
        fetchPolicy: 'network-only',
      });
      if (version !== policyVersion.current) return;
      if (result.error) throw result.error;
      if (!result.data?.getReturnPolicy)
        throw new Error(
          'Return eligibility could not be loaded. Please try again.',
        );
      const projectedOrder = projectReturnOrderMoney(order, result.data.getReturnPolicy.returnMoney, storeProperties?.currency);
      if (!projectedOrder) throw new Error('Authoritative return currency amounts are unavailable. Contact ShipAid support for review.');
      setReturnMoneyOrder(projectedOrder);
      setPolicy(result.data.getReturnPolicy);
    } catch (error) {
      if (version === policyVersion.current) setError(errorMessage(error));
    } finally {
      if (version === policyVersion.current) setLoadingPolicy(false);
    }
  }, [getPolicy, order, storeId, storeProperties?.currency]);
  useEffect(() => {
    if (type === 'RETURN') loadPolicy();
    else setLoadingPolicy(false);
    return invalidatePolicy;
  }, [type, loadPolicy, invalidatePolicy]);

  const reasonSettings = {
    policySettings:
      order?.store?.policySettings || storeProperties?.policySettings,
    resolvePortalOptions:
      order?.store?.resolvePortalOptions ||
      storeProperties?.resolvePortalOptions,
  };
  const defectReview =
    type === 'RETURN' && isDefectReturn(items, reasonSettings);
  const steps: Step[] = [
    'Order',
    'Request type',
    'Items & details',
    ...(type === 'RETURN' && !defectReview ? ['Return options' as Step] : []),
    'Review request',
  ];
  const stepIndex = steps.indexOf(step);
  const protectionStatus = order?.protectionStatus?.trim().toLowerCase();
  const protectedOrder =
    protectionStatus === 'unprotected'
      ? false
      : protectionStatus === 'protected' ||
        Number(order?.protectionTotal) > 0 ||
        Boolean(order?.isReorder);
  const returnsEnabled = getNewRequestReturnsEnabled(
    reasonSettings.policySettings,
    reasonSettings.resolvePortalOptions,
  );
  const protectionVariants =
    storeProperties?.protectionSettings?.variantIds || [];
  const visibleItems = (order?.items || []).filter(
    (item) => !protectionVariants.includes(item.variantId || ''),
  );
  const availableQuantity = (item: RequestOrderItem) => {
    if (type !== 'RETURN') return issueAvailableQuantity(item);
    const eligibility = policy?.items.find(
      (entry) => entry.orderItemId === item.id,
    );
    return policy?.eligible && eligibility?.eligible
      ? Math.max(
          0,
          Math.min(
            item.quantity,
            eligibility.availableQuantity,
            eligibility.maxReturnQuantity ?? eligibility.availableQuantity,
          ),
        )
      : 0;
  };
  const allowedReasons = getNewReturnReasons(policy, reasonSettings);
  const evidenceFor = (itemReason: string) =>
    getNewReturnEvidence(policy, itemReason, reasonSettings);
  const validItems =
    items.length > 0 &&
    items.every((item) => {
      const source = visibleItems.find((entry) => entry.id === item.id);
      if (
        !source ||
        !Number.isInteger(item.quantity) ||
        item.quantity < 1 ||
        item.quantity > availableQuantity(source)
      )
        return false;
      if (type !== 'RETURN')
        return issueReasons.some((entry) => entry.id === reason);
      const evidence = evidenceFor(item.reason);
      return (
        allowedReasons.some((entry) => entry.id === item.reason) &&
        (evidence.notes !== 'REQUIRED' ||
          Boolean(
            item.reasonComments?.trim() || (!evidence.defect && notes.trim()),
          )) &&
        (evidence.photos !== 'REQUIRED' || images.some(isCompletedReturnPhoto))
      );
    }) &&
    (type !== 'RETURN' || (returnsEnabled && Boolean(policy?.eligible))) &&
    (type !== 'ISSUE' || protectedOrder);

  const updateItem = (id: string, changes: Partial<NewRequestItemInput>) => {
    setItems((current) =>
      current.map((item) => (item.id === id ? { ...item, ...changes } : item)),
    );
    setReturnSelection(null);
  };
  const changeType = (nextType: RequestType) => {
    if (type === nextType) return;
    setType(nextType);
    setItems([]);
    setReason('');
    setNotes('');
    setImages([]);
    setReturnSelection(null);
    setError('');
  };
  const previous = () => {
    setError('');
    setStep(steps[Math.max(0, stepIndex - 1)]);
  };
  const submit = async () => {
    if (
      submissionLock.current ||
      !order ||
      !storeId ||
      !validItems ||
      (type === 'RETURN' && !defectReview && !returnSelection)
    )
      return;
    submissionLock.current = true;
    setSubmitting(true);
    setError('');
    try {
      if (type === 'RETURN') {
        const {
          requestedResolution,
          selectedShippingOption,
          selectedDeflectionOffer,
          deflectionOption,
          expectedFeeQuoteFingerprint,
        } = returnSelection || {};
        const result = await startReturn({
          variables: {
            input: {
              storeId,
              orderId: order.id,
              customerEmail: order.customerEmail || undefined,
              notes,
              ...(!defectReview
                ? {
                    requestedResolution,
                    selectedShippingOption,
                    selectedDeflectionOffer,
                    deflectionOption,
                    expectedFeeQuoteFingerprint,
                  }
                : {}),
              items: items.map((item) => ({
                ...item,
                ...(!defectReview ? { requestedResolution } : {}),
              })),
              ...(images.length ? { images } : {}),
            },
          },
        });
        if (result.errors?.length) throw result.errors[0];
        const id = result.data?.startReturn?.id;
        if (!id)
          throw new Error(
            'The Return could not be confirmed. Check the request list before trying again.',
          );
        navigate(withRequestReturnTo(`/returns/${id}`, returnTo), {
          state: { returnLocation: returnTo },
        });
      } else {
        const result = await startClaim({
          variables: {
            input: {
              storeId,
              orderId: order.id,
              reason,
              notes,
              items: items.map(({ id, quantity }) => ({
                id,
                quantity,
                reason,
              })),
              ...(images.length
                ? { images: images.map((url) => ({ url, notes })) }
                : {}),
            },
          },
        });
        if (result.errors?.length) throw result.errors[0];
        const id = result.data?.startClaim?.[0]?.id;
        if (!id)
          throw new Error(
            'The Issue could not be confirmed. Check the request list before trying again.',
          );
        setClaimId(id);
      }
    } catch (error) {
      setError(errorMessage(error));
      submissionLock.current = false;
      setSubmitting(false);
    }
  };

  if (!storeId)
    return <p className="p-6">Choose a store to create an Issue or Return.</p>;
  if (claimId && order)
    return (
      <div className="max-w-7xl mx-auto px-4 pb-8 sm:px-6 md:px-8">
        <NewIssueResolution
          claimId={claimId}
          order={order as unknown as Order}
          returnTo={returnTo}
        />
      </div>
    );
  return (
    <div className="max-w-7xl mx-auto px-4 pb-8 sm:px-6 md:px-8">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">New request</h2>
          <p className="mt-1 text-sm text-gray-600">
            Create a delivery Issue or a product Return for a customer.
          </p>
        </div>
        <Button
          variant="outlined"
          disabled={submitting}
          onClick={() => navigate(returnTo)}
        >
          Cancel
        </Button>
      </div>
      <nav
        aria-label="Request creation progress"
        className="my-6 overflow-x-auto"
      >
        <ol className="flex min-w-max gap-5 text-sm">
          {steps.map((name, index) => (
            <li
              key={name}
              aria-current={step === name ? 'step' : undefined}
              className={classNames(
                'flex items-center gap-2',
                index === stepIndex
                  ? 'font-semibold text-blue-700'
                  : 'text-gray-500',
              )}
            >
              <span
                className={classNames(
                  'flex h-6 w-6 items-center justify-center rounded-full text-xs',
                  index <= stepIndex ? 'bg-blue-700 text-white' : 'bg-gray-100',
                )}
              >
                {index + 1}
              </span>
              {name}
            </li>
          ))}
        </ol>
      </nav>
      {order && step !== 'Order' && (
        <div className="mb-5 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm">
          <div>
            <strong>Order {order.number}</strong>
            <span className="ml-3 text-gray-600">
              {order.customerName} ·{' '}
              {order.customerEmail || 'No customer email'}
            </span>
          </div>
          <span className="text-gray-600">
            {type === 'RETURN'
              ? 'Product return'
              : type === 'ISSUE'
              ? 'Delivery issue'
              : protectedOrder
              ? 'Guaranteed order'
              : 'Order without delivery guarantee'}
          </span>
        </div>
      )}
      {error && (
        <div
          role="alert"
          className="mb-4 rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-800"
        >
          {error}
        </div>
      )}
      <section
        aria-labelledby="request-step-title"
        className="rounded-lg border border-gray-200 bg-white p-4 sm:p-6"
      >
        <h3
          id="request-step-title"
          className="mb-4 text-lg font-semibold text-gray-900"
        >
          {step === 'Order' ? 'Find an order' : step}
        </h3>
        {step === 'Order' && (
          <>
            <form
              onSubmit={(event) => {
                event.preventDefault();
                setOrderIdToLoad(undefined);
                loadOrders(search);
              }}
              className="mb-5 flex items-end gap-3"
            >
              <label className="block w-full max-w-lg text-sm font-medium">
                Order number or customer email
                <input
                  type="search"
                  className={fieldClass}
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="e.g. #1001 or customer@example.com"
                />
              </label>
              <Button type="submit" isLoading={loadingOrders}>
                Search
              </Button>
            </form>
            {loadingOrders ? (
              <p role="status" className="text-sm text-gray-600">
                Loading orders…
              </p>
            ) : error ? (
              <Button
                variant="outlined"
                onClick={() =>
                  orderIdToLoad ? loadOrder(orderIdToLoad) : loadOrders(search)
                }
              >
                {orderIdToLoad ? 'Retry order' : 'Retry search'}
              </Button>
            ) : orders.length ? (
              <ul className="divide-y divide-gray-200">
                {orders.map((entry) => (
                  <li
                    key={entry.id}
                    className="flex flex-wrap items-center justify-between gap-3 py-3"
                  >
                    <div>
                      <p className="text-sm font-semibold">{entry.number}</p>
                      <p className="text-sm text-gray-600">
                        {entry.customerName} ·{' '}
                        {entry.customerEmail || 'No customer email'}
                      </p>
                    </div>
                    <Button
                      variant="outlined"
                      onClick={() => loadOrder(entry.id)}
                      props={{ 'aria-label': `Select order ${entry.number}` }}
                    >
                      Select order
                    </Button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-gray-600">
                No orders found. Try another order number or customer email.
              </p>
            )}
          </>
        )}
        {step === 'Request type' && (
          <fieldset className="grid gap-4 sm:grid-cols-2">
            <legend className="sr-only">Choose the request type</legend>
            {[
              {
                value: 'ISSUE' as const,
                title: 'Delivery issue',
                description:
                  'Lost, stolen, damaged, or late delivery. Review, replace, refund, or resolve the Issue after creation.',
              },
              {
                value: 'RETURN' as const,
                title: 'Return',
                description:
                  'Product returns, fit, wrong items, or changed minds. Check eligibility, choose an outcome, and review return shipping and fees.',
              },
            ].map((option) => (
              <label
                key={option.value}
                className={classNames(
                  'flex cursor-pointer items-start gap-3 rounded-lg border p-4',
                  type === option.value
                    ? 'border-blue-600 bg-blue-50'
                    : 'border-gray-200',
                  (option.value === 'ISSUE' ? !protectedOrder : !returnsEnabled)
                    ? 'opacity-60'
                    : '',
                )}
              >
                <input
                  className="mt-1 text-blue-600"
                  type="radio"
                  name="request-type"
                  aria-label={option.title}
                  checked={type === option.value}
                  disabled={
                    option.value === 'ISSUE' ? !protectedOrder : !returnsEnabled
                  }
                  onChange={() => changeType(option.value)}
                />
                <span>
                  <span className="block font-semibold text-gray-900">
                    {option.title}
                  </span>
                  <span className="mt-1 block text-sm text-gray-600">
                    {option.description}
                  </span>
                  {option.value === 'ISSUE' && !protectedOrder && (
                    <span className="mt-2 block text-sm">
                      A delivery guarantee is required for an Issue. Return
                      eligibility is checked separately.
                    </span>
                  )}
                  {option.value === 'RETURN' && !returnsEnabled && (
                    <span className="mt-2 block text-sm">
                      Return filing is disabled for this store. Enable it in
                      Resolution Portal settings to create a Return.
                    </span>
                  )}
                </span>
              </label>
            ))}
          </fieldset>
        )}
        {step === 'Items & details' && (
          <>
            {type === 'RETURN' &&
              (loadingPolicy ? (
                <p role="status" className="mb-4 text-sm text-gray-600">
                  Checking return eligibility…
                </p>
              ) : (
                <div className="mb-4">
                  <p
                    className={classNames(
                      'mb-2 text-sm',
                      policy?.eligible ? 'text-gray-600' : 'text-red-700',
                    )}
                  >
                    {policy?.eligible
                      ? 'Choose eligible items and a reason for each. Available quantities account for existing requests.'
                      : policy?.ineligibleReason ||
                        'Return eligibility is unavailable.'}
                  </p>
                  <Button variant="outlined" onClick={loadPolicy}>
                    Check eligibility again
                  </Button>
                </div>
              ))}
            {!visibleItems.length && (
              <p className="text-sm text-gray-600">
                There are no requestable items on this order.
              </p>
            )}
            <div className="divide-y divide-gray-200">
              {visibleItems.map((source) => {
                const selected = items.find((item) => item.id === source.id);
                const available = availableQuantity(source);
                const eligibility = policy?.items.find(
                  (item) => item.orderItemId === source.id,
                );
                const selectedEvidence = selected
                  ? evidenceFor(selected.reason)
                  : null;
                return (
                  <div key={source.id} className="py-4 first:pt-0">
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        className="mt-1 rounded text-blue-600"
                        aria-label={`Select ${source.title}`}
                        checked={Boolean(selected)}
                        disabled={!available || loadingPolicy}
                        onChange={() => {
                          setItems((current) =>
                            selected
                              ? current.filter((item) => item.id !== source.id)
                              : [
                                  ...current,
                                  { id: source.id, quantity: 1, reason: '' },
                                ],
                          );
                          setReturnSelection(null);
                        }}
                      />
                      {source.imageSrc && (
                        <img
                          src={source.imageSrc}
                          alt=""
                          className="h-12 w-12 rounded object-cover"
                        />
                      )}
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-gray-900">
                          {source.title}
                        </p>
                        <p className="mt-1 text-xs text-gray-500">
                          {available} of {source.quantity} available
                          {source.sku ? ` · ${source.sku}` : ''}
                        </p>
                        {!available && (
                          <p className="mt-1 text-sm text-gray-600">
                            {type === 'RETURN' && eligibility?.ineligibleReason
                              ? eligibility.ineligibleReason
                              : 'No quantity available for this request.'}
                          </p>
                        )}
                      </div>
                      {selected && (
                        <label className="w-24 text-xs font-medium">
                          Quantity
                          <NonNegativeNumberInput
                            aria-label={`Quantity for ${source.title}`}
                            min={1}
                            max={available}
                            step={1}
                            className={fieldClass}
                            value={selected.quantity || ''}
                            onChange={(event) =>
                              updateItem(source.id, {
                                quantity: Number(event.target.value),
                              })
                            }
                          />
                        </label>
                      )}
                    </div>
                    {selected && type === 'RETURN' && (
                      <div className="mt-4 grid gap-4 pl-7 sm:grid-cols-2">
                        <label className="text-sm font-medium">
                          Return reason
                          <select
                            aria-label={`Return reason for ${source.title}`}
                            className={fieldClass}
                            value={selected.reason}
                            onChange={(event) =>
                              updateItem(source.id, {
                                reason: event.target.value,
                              })
                            }
                          >
                            <option value="">Select a reason</option>
                            {allowedReasons.map((reason) => (
                              <option key={reason.id} value={reason.id}>
                                {reason.label}
                              </option>
                            ))}
                          </select>
                        </label>
                        <label className="text-sm font-medium">
                          Details
                          {selectedEvidence?.notes === 'REQUIRED'
                            ? selectedEvidence.defect
                              ? ' (required)'
                              : ' (required unless request notes are provided)'
                            : ' (optional)'}
                          <textarea
                            aria-label={`Details for ${source.title}`}
                            rows={2}
                            maxLength={1000}
                            className={fieldClass}
                            value={selected.reasonComments || ''}
                            onChange={(event) =>
                              updateItem(source.id, {
                                reasonComments: event.target.value,
                              })
                            }
                          />
                        </label>
                        {selectedEvidence?.photos === 'REQUIRED' && (
                          <p className="text-sm text-gray-600 sm:col-span-2">
                            A photo is required for this return reason. Attach
                            it below.
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            {type === 'ISSUE' && (
              <label className="mt-5 block max-w-md text-sm font-medium">
                Issue reason
                <select
                  className={fieldClass}
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                >
                  <option value="">Select a reason</option>
                  {issueReasons.map((reason) => (
                    <option key={reason.id} value={reason.id}>
                      {reason.label}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <label className="mt-5 block text-sm font-medium">
              Notes
              <textarea
                rows={3}
                maxLength={1000}
                className={fieldClass}
                placeholder="Add helpful context for this request"
                value={notes}
                onChange={(event) => setNotes(event.target.value)}
              />
            </label>
            <details
              className="mt-5 rounded-md border border-gray-200 p-3"
              open={
                images.length > 0 ||
                items.some(
                  (item) => evidenceFor(item.reason).photos === 'REQUIRED',
                )
              }
            >
              <summary className="cursor-pointer text-sm font-medium">
                Photos ({images.length}/5)
              </summary>
              <div className="mt-4 overflow-x-auto">
                <ClaimsImagesUpload
                  claimImages={images.map((url) => ({ id: url, url }))}
                  onUpload={({ urls }) =>
                    setImages((current) =>
                      Array.from(new Set([...current, ...urls])).slice(0, 5),
                    )
                  }
                  onRemoveImage={(url) =>
                    setImages((current) =>
                      current.filter((image) => image !== url),
                    )
                  }
                />
              </div>
            </details>
            {!validItems && (
              <p className="mt-4 text-sm text-gray-600">
                Select at least one available item, enter a valid quantity, and
                complete the required details to continue.
              </p>
            )}
          </>
        )}
        {step === 'Return options' && order && policy && (
          <ReturnRequestOptions
            order={returnMoneyOrder || order}
            items={items}
            notes={notes}
            policy={policy}
            onContinue={(selection) => {
              setReturnSelection(selection);
              setStep('Review request');
              setError('');
            }}
          />
        )}
        {step === 'Review request' && (
          <>
            <p className="mb-4 text-sm text-gray-600">
              Check the details before creating this{' '}
              {type === 'RETURN' ? 'Return' : 'Issue'}.
            </p>
            <ul className="divide-y divide-gray-100">
              {items.map((item) => (
                <li
                  className="flex justify-between gap-4 py-3 text-sm"
                  key={item.id}
                >
                  <div>
                    <p className="font-medium">
                      {
                        visibleItems.find((source) => source.id === item.id)
                          ?.title
                      }
                    </p>
                    <p className="text-gray-600">
                      {type === 'RETURN'
                        ? allowedReasons.find(
                            (reason) => reason.id === item.reason,
                          )?.label
                        : issueReasons.find((entry) => entry.id === reason)
                            ?.label}
                    </p>
                    {item.reasonComments && (
                      <p className="mt-1 text-gray-600">
                        {item.reasonComments}
                      </p>
                    )}
                  </div>
                  <span>Qty {item.quantity}</span>
                </li>
              ))}
            </ul>
            {notes && (
              <div className="mt-4 text-sm">
                <h4 className="font-medium">Notes</h4>
                <p className="mt-1 whitespace-pre-wrap text-gray-600">
                  {notes}
                </p>
              </div>
            )}
            {images.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-2">
                {images.map((url, index) => (
                  <img
                    key={url}
                    src={url}
                    alt={`Attached evidence ${index + 1}`}
                    className="h-16 w-16 rounded object-cover"
                  />
                ))}
              </div>
            )}
            {defectReview ? (
              <p className="mt-5 rounded-md bg-blue-50 p-4 text-sm text-blue-900">
                This Return requires evidence review. A merchant will review the
                damage or defect and decide the next steps before shipping or a
                financial resolution is arranged.
              </p>
            ) : type === 'RETURN' && returnSelection ? (
              <div className="mt-5 rounded-md bg-gray-50 p-4 text-sm">
                <dl className="grid gap-3 sm:grid-cols-2">
                  <div>
                    <dt className="text-gray-500">Requested outcome</dt>
                    <dd className="mt-1 font-medium">
                      {returnSelection.resolutionLabel}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-gray-500">Return shipping</dt>
                    <dd className="mt-1 font-medium">
                      {returnSelection.shippingLabel}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-gray-500">Estimated fees</dt>
                    <dd className="mt-1 font-medium">
                      {returnSelection.feeQuote.currency}{' '}
                      {formatReturnDecimal(Number(returnSelection.feeQuote.totalFees), returnSelection.feeQuote.currency)}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-gray-500">Return value after fees</dt>
                    <dd className="mt-1 font-medium">
                      {returnSelection.feeQuote.currency}{' '}
                      {formatReturnDecimal(Number(returnSelection.feeQuote.netAmount), returnSelection.feeQuote.currency)}
                    </dd>
                  </div>
                </dl>
                <p className="mt-4 text-gray-600">
                  {policy?.approvalMode === 'AUTO_DENY'
                    ? 'This request is set to be declined by the current return policy.'
                    : policy?.approvalMode === 'MANUAL_REVIEW'
                    ? 'This Return requires merchant review. Continue in Return details to approve and manage the next steps.'
                    : policy?.approvalMode === 'AUTO_APPROVE'
                    ? 'Automatic approval is enabled. Creating this Return may create a shipping label or issue a selected keep-item reward immediately.'
                    : 'Your return policy determines approval and the next steps.'}{' '}
                  Shipping, inspection, and settlement follow your configured
                  return policy.
                </p>
              </div>
            ) : type === 'RETURN' ? (
              <p className="mt-5 rounded-md bg-gray-50 p-4 text-sm text-gray-600">
                Choose return options before creating this request.
              </p>
            ) : (
              <p className="mt-5 rounded-md bg-gray-50 p-4 text-sm text-gray-600">
                After creation, review the Issue and choose a resolution. Your
                configured Issue automation may also apply.
              </p>
            )}
          </>
        )}
      </section>
      <div className="mt-5 flex items-center justify-between gap-3">
        <div>
          {stepIndex > 0 && (
            <Button variant="outlined" disabled={submitting} onClick={previous}>
              Previous
            </Button>
          )}
        </div>
        <p className="text-sm text-gray-500">
          Step {stepIndex + 1} of {steps.length}
        </p>
        {step === 'Review request' ? (
          <Button
            onClick={submit}
            isLoading={submitting}
            disabled={
              !validItems ||
              (type === 'RETURN' && !defectReview && !returnSelection)
            }
          >
            {type === 'RETURN' ? 'Create return' : 'Create issue'}
          </Button>
        ) : step === 'Request type' || step === 'Items & details' ? (
          <Button
            disabled={
              step === 'Request type'
                ? !type || (type === 'RETURN' && isMixedReturnCurrency(order, storeProperties?.currency) && !returnMoneyOrder)
                : !validItems || loadingPolicy
            }
            onClick={() => {
              setError('');
              setStep(steps[stepIndex + 1]);
            }}
          >
            Next
          </Button>
        ) : (
          <div />
        )}
      </div>
    </div>
  );
}

export const NewClaim = () => {
  const { storeId } = useStore();
  return <NewRequestFlow key={storeId} />;
};
